<template>
  <div class="min-h-screen bg-[#F5F5F7] font-sans text-[#1D1D1F] antialiased">
    
    <header class="pt-24 pb-16 px-6 text-center bg-white/80 backdrop-blur-xl mt-8 top-0 z-50 border-b border-gray-200/50">
      <h1 class="text-5xl md:text-7xl font-bold tracking-tight mb-6 bg-gradient-to-b from-[#1D1D1F] to-[#86868B] bg-clip-text text-transparent">
        Nos Formations
      </h1>
      <p class="text-xl md:text-2xl text-[#86868B] max-w-3xl mx-auto mb-10 font-medium">
        Découvrez notre catalogue complet de formations professionnelles conçues pour propulser votre carrière au niveau supérieur.
      </p>
      <div class="flex flex-col sm:flex-row justify-center gap-5">
        <button class="px-10 py-4 bg-[#0071E3] text-white rounded-full font-semibold hover:bg-[#0077ED] shadow-lg shadow-blue-500/20 transition-all active:scale-95">
          Explorer le catalogue
        </button>
        <router-link :to="{name: 'ContactView'}" class="px-10 py-4 bg-white/80 text-[#1D1D1F] border border-gray-300 rounded-full font-semibold hover:bg-white transition-all active:scale-95 shadow-sm">
          Demander un conseil
        </router-link>
      </div>
    </header>

    <main class="max-w-7xl mx-auto px-6 py-16">
      
      <div class="flex flex-col md:flex-row items-center justify-between mb-16 gap-6">
        <div class="flex gap-3 overflow-x-auto pb-4 w-full md:w-auto no-scrollbar">
          <button v-for="cat in categories" :key="cat" 
            @click="selectedCat = cat"
            class="whitespace-nowrap px-6 py-2.5 rounded-full text-sm font-medium transition-all duration-300"
            :class="selectedCat === cat ? 'bg-[#1D1D1F] text-white shadow-md' : 'bg-white text-[#1D1D1F] border border-gray-200 hover:bg-gray-50'">
            {{ cat }}
          </button>
        </div>
        <div class="flex items-center gap-3 text-sm px-4 py-2 bg-white rounded-full border border-gray-200">
          <span class="text-[#86868B]">Trier par :</span>
          <select class="bg-transparent font-bold text-[#1D1D1F] outline-none cursor-pointer">
            <option>Plus récent</option>
            <option>Titre</option>
            <option>Populaire</option>
          </select>
        </div>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
        <div v-for="formation in filteredFormations" :key="formation.title" 
          class="group bg-white rounded-[2.5rem] p-8 shadow-[0_4px_20px_rgba(0,0,0,0.03)] hover:shadow-[0_20px_40px_rgba(0,0,0,0.08)] transition-all duration-500 transform hover:-translate-y-2 border border-gray-100/50">
          
          <div class="flex justify-between items-start mb-8">
            <div class="p-3 bg-[#F5F5F7] rounded-2xl text-2xl group-hover:scale-110 transition-transform duration-500">
              {{ formation.icon }}
            </div>
            <div class="flex flex-col items-end">
              <span class="px-3 py-1 bg-blue-50 text-[#0071E3] text-[10px] font-bold rounded-full uppercase tracking-tighter">
                {{ formation.level }}
              </span>
              <div class="text-[#86868B] text-xs mt-2 flex items-center gap-1 font-medium">
                <span class="text-yellow-400 text-sm">★</span> {{ formation.rating }} ({{ formation.reviews }})
              </div>
            </div>
          </div>
          
          <h3 class="text-2xl font-bold mb-3 tracking-tight group-hover:text-[#0071E3] transition-colors">
            {{ formation.title }}
          </h3>
          <p class="text-[#86868B] mb-8 text-sm leading-relaxed h-12 overflow-hidden">
            {{ formation.description }}
          </p>
          
          <div class="mb-8">
            
            <div class="bg-[#F5F5F7]/50 p-4 w-full rounded-3xl border border-gray-50 text-center">
              <p class="text-[9px] uppercase text-[#86868B] font-extrabold tracking-widest mb-1">Insertion</p>
              <p class="font-bold text-lg text-green-600">{{ formation.successRate }}</p>
            </div>
          </div>

          <div class="space-y-3">
            <a 
  href="https://wa.me/+237656531225"
  target="_blank"
  class="w-full py-4 bg-[#1D1D1F] text-white rounded-2xl font-semibold hover:bg-black transition-all active:scale-95 flex items-center justify-center gap-2"
>
  <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
    <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981z"/>
  </svg>
  Contactez-Nous sur WhatsApp
</a>
           
          </div>
        </div>
      </div>

      <section class="mt-40">
        <div class="text-center mb-20">
          <h2 class="text-4xl md:text-5xl font-bold mb-6 tracking-tight">Nos Méthodes d'Apprentissage</h2>
          <p class="text-xl text-[#86868B]">Des formats flexibles adaptés à vos contraintes professionnelles.</p>
        </div>
        
        <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div v-for="method in methods" :key="method.title" 
            class="bg-white p-12 rounded-[3rem] border border-gray-100 hover:shadow-2xl transition-shadow duration-700">
            <div class="w-16 h-16 bg-[#F5F5F7] rounded-[1.5rem] flex items-center justify-center mb-8 text-3xl shadow-inner">
              {{ method.icon }}
            </div>
            <h4 class="text-2xl font-bold mb-5 tracking-tight">{{ method.title }}</h4>
            <p class="text-[#86868B] leading-relaxed text-lg font-light">
              {{ method.desc }}
            </p>
          </div>
        </div>
      </section>

      <section class="mt-40 bg-[#1D1D1F] rounded-[4rem] p-10 md:p-24 text-white overflow-hidden relative">
        <div class="absolute top-0 right-0 w-64 h-64 bg-blue-600 rounded-full blur-[120px] opacity-20"></div>
        <h2 class="text-4xl font-bold mb-16 relative z-10">Ils nous font confiance</h2>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-10 relative z-10">
          <div v-for="review in reviews" :key="review.author" class="bg-white/5 backdrop-blur-xl p-10 rounded-[2.5rem] border border-white/10">
            <div class="flex items-center gap-5 mb-8">
              <div class="w-14 h-14 bg-gradient-to-tr from-[#0071E3] to-[#5AC8FA] rounded-full flex items-center justify-center font-bold text-xl">
                {{ review.initials }}
              </div>
              <div>
                <p class="font-bold text-xl">{{ review.author }}</p>
                <p class="text-sm text-gray-400 font-medium">Ancien apprenant</p>
              </div>
            </div>
            <p class="text-xl leading-relaxed text-gray-300 font-light">"{{ review.text }}"</p>
            <div class="mt-6 flex text-yellow-400 text-sm">
              ★★★★★
            </div>
          </div>
        </div>
      </section>
    </main>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';

const selectedCat = ref('Toutes les formations');
const categories = ['Toutes les formations', 'Langues', 'Marketing Digital', 'Informatique', 'Bureautique'];

const formations = [
  {
    title: "Allemand",
    category: "Langues",
    level: "A1-C1",
    icon: "🌍",
    description: "Programmes accélérés pour réussir vos tests de langue (Goethe) et faciliter votre mobilité.",
    instructors: "13",
    successRate: "98%",
    rating: "4.9",
    reviews: "1.2k"
  },
  {
    title: "Anglais Intensif",
    category: "Langues",
    level: "A1-C1",
    icon: "🇬🇧",
    description: "Programmes accélérés pour maîtriser la langue de Shakespeare et réussir vos tests internationaux (IELTS, TOEFL, Cambridge, TOEIC).",
    instructors: "13",
    successRate: "98%",
    rating: "4.9",
    reviews: "1.2k"
  },
    {
    title: "Italien Intensif",
    category: "Langues",
    level: "A1-C1",
    icon: "🇮🇹",
    description: "Plongez dans la langue de Dante et préparez avec succès vos certifications officielles (CILS, CELI, PLIDA) pour vos projets en Italie.",
    instructors: "8", 
    successRate: "99%",
    rating: "4.8",
    reviews: "5k" 
  },
  {
    title: "Marketing Digital 360",
    category: "Marketing Digital",
    level: "Expert",
    icon: "📈",
    description: "Maîtrisez le SEO, la publicité Meta/Google et le Community Management pour booster n'importe quel business.",
    instructors: "5",
    successRate: "100%",
    rating: "5",
    reviews: "5k"
  },
  {
    title: "Secrétariat Bureautique",
    category: "Bureautique",
    level: "Pro",
    icon: "💻",
    description: "Maîtrisez parfaitement le Pack Office (Excel, Word) et les outils de gestion administrative moderne.",
    instructors: "4",
    successRate: "100%",
    rating: "5",
    reviews: "4800"
  },
  {
    title: "Maintenance Informatique",
    category: "Informatique",
    level: "Technicien",
    icon: "🛠️",
    description: "Apprenez à diagnostiquer, réparer et optimiser les parcs informatiques matériels et logiciels.",
    instructors: "3",
    successRate: "99%",
    rating: "4.9",
    reviews: "5k"
  },
  {
    title: "Développement Web",
    category: "Informatique",
    level: "Fullstack",
    icon: "🌐",
    description: "Créez des sites web modernes et des applications performantes avec les derniers frameworks.",
    instructors: "6",
    successRate: "100%",
    rating: "4.9",
    reviews: "6k"
  }
];

const filteredFormations = computed(() => {
  if (selectedCat.value === 'Toutes les formations') return formations;
  return formations.filter(f => f.category === selectedCat.value);
});

const methods = [
  { 
    title: "Présentiel (En Centre)", 
    icon: "🏢", 
    desc: "Plongez dans un environnement professionnel à Douala ou Bafoussam. Interaction directe avec des experts et accès à nos équipements de pointe." 
  },
  { 
    title: "À Distance (100% Online)", 
    icon: "☁️", 
    desc: "La flexibilité absolue. Suivez nos cours en direct ou en replay via notre plateforme e-learning, où que vous soyez au Cameroun ou ailleurs." 
  },
  { 
    title: "Format Hybride", 
    icon: "🌓", 
    desc: "Le meilleur compromis : bénéficiez de la puissance du présentiel pour la pratique et de la flexibilité du digital pour la théorie." 
  }
];

const reviews = [
  { author: "Naomi Moupe", initials: "NA", text: "L'encadrement à l'Institut Fuchs est exceptionnel. J'ai pu obtenir mon B1 en un temps record pour mon projet de voyage." },
  { author: "Talla KTBM12", initials: "TA", text: "Le meilleur centre à Bafoussam ! La formation en Marketing Digital a totalement changé ma vision du business." }
];
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');

.no-scrollbar::-webkit-scrollbar {
  display: none;
}
.no-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
</style>